import { useEffect, useRef } from 'react'

// Sem site key configurada (dev local -- ver README/backend app.turnstile.enabled), o
// componente não renderiza nada e nunca chama onToken; o backend também não exige o token
// nesse caso, então o formulário continua funcionando normalmente sem CAPTCHA.
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string
        callback: (token: string) => void
        'error-callback'?: () => void
        'expired-callback'?: () => void
      }) => string
      reset: (widgetId?: string) => void
    }
  }
}

let scriptPromise: Promise<void> | null = null
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Falha ao carregar verificação de segurança'))
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

// resetKey: sempre que muda, o widget é resetado e um novo token é pedido. O token do
// Turnstile é de uso único (consumido na verificação do backend), então numa tela de login
// -- onde errar a senha é comum -- a página incrementa esse valor a cada tentativa falha
// pra não deixar o usuário preso com um token já gasto.
export function TurnstileWidget({ onToken, resetKey }: { onToken: (token: string | null) => void; resetKey?: number }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!SITE_KEY) return
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken(token),
          'error-callback': () => onToken(null),
          'expired-callback': () => onToken(null),
        })
      })
      .catch(() => onToken(null))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (resetKey === undefined || !widgetIdRef.current || !window.turnstile) return
    window.turnstile.reset(widgetIdRef.current)
    onToken(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  if (!SITE_KEY) return null
  return <div ref={containerRef} />
}
